import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { articles, categories, topics } from "@/mocks/articles";
import { ArticleRow } from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 5;

interface CategoryPageViewProps {
  categoryKey: "news" | "lifestyle" | "trend";
  showTopics?: boolean;
}

export default function CategoryPageView({ categoryKey, showTopics = false }: CategoryPageViewProps) {
  const meta = categories.find((item) => item.key === categoryKey)!;
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get("tag") ?? "";
  const [timeRange, setTimeRange] = useState<"all" | "month" | "half">("all");
  const [sortBy, setSortBy] = useState<"latest" | "views">("latest");
  const [page, setPage] = useState(1);

  useSeo({
    title: meta.title,
    description: meta.description,
    keywords: `台北娜娜,${meta.name},时尚生活,潮流资讯`,
  });

  const categoryArticles = useMemo(
    () => articles.filter((item) => item.category === categoryKey),
    [categoryKey],
  );

  const tagOptions = useMemo(() => {
    const set = new Set<string>();
    categoryArticles.forEach((item) => item.tags.forEach((tag) => set.add(tag)));
    return Array.from(set);
  }, [categoryArticles]);

  const list = useMemo(() => {
    let result = [...categoryArticles];
    if (activeTag) {
      result = result.filter((item) => item.tags.includes(activeTag));
    }
    if (meta.filterMode === "time" && timeRange !== "all") {
      const days = timeRange === "month" ? 30 : 182;
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      result = result.filter((item) => new Date(item.date).getTime() >= cutoff);
    }
    result.sort((a, b) =>
      sortBy === "views" ? b.views - a.views : a.date < b.date ? 1 : -1,
    );
    return result;
  }, [categoryArticles, activeTag, timeRange, sortBy]);

  useEffect(() => {
    setPage(1);
  }, [activeTag, timeRange, sortBy]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const paged = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const updateTag = (tag: string) => {
    const next = new URLSearchParams(searchParams);
    if (tag) next.set("tag", tag);
    else next.delete("tag");
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-primary-900 text-background-50">
        <img
          src={meta.cover}
          alt={meta.name}
          title={`${meta.name} - 台北娜娜`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/70 to-primary-950/40" />
        <div className="relative px-4 pb-12 pt-28 md:px-8 md:pb-16 md:pt-32 lg:px-12">
          <nav className="flex items-center gap-2 text-xs text-background-50/60">
            <Link to="/" className="cursor-pointer transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-50">{meta.name}</span>
          </nav>
          <h1 className="mt-5 font-heading text-3xl font-extrabold md:text-4xl">{meta.name}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-50/80 md:text-base">
            {meta.intro}
          </p>
        </div>
      </section>

      {showTopics && (
        <section className="bg-background-100 px-4 py-10 md:px-8 md:py-12 lg:px-12">
          <div className="mb-6 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-50">
              <i className="ri-bookmark-3-line text-lg" />
            </span>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">潮流专题合集</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {topics.map((topic) => (
              <div
                key={topic.id}
                className="group overflow-hidden rounded-xl border border-background-200 bg-background-50"
              >
                <div className="aspect-[4/5] w-full overflow-hidden">
                  <img
                    src={topic.image}
                    alt={topic.title}
                    title={`${topic.title} - 台北娜娜`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{topic.title}</h3>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                    {topic.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="bg-background-50 px-4 py-12 md:px-8 md:py-16 lg:px-12">
        <div className="mb-8 flex flex-col gap-5 border-b border-background-200 pb-6">
          <div className="flex flex-col gap-3">
            {meta.filterMode === "time" && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-sm font-medium text-foreground-500">时间</span>
                {[
                  { key: "all", label: "全部时间" },
                  { key: "month", label: "近一月" },
                  { key: "half", label: "近半年" },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setTimeRange(option.key as "all" | "month" | "half")}
                    className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                      timeRange === option.key
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-medium text-foreground-500">主题</span>
              <button
                type="button"
                onClick={() => updateTag("")}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                  activeTag === ""
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                全部主题
              </button>
              {tagOptions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => updateTag(tag)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    activeTag === tag
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-500">
              共 <span className="font-semibold text-foreground-900">{list.length}</span> 篇内容
            </p>
            <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1">
              {[
                { key: "latest", label: "最新发布" },
                { key: "views", label: "浏览量" },
              ].map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSortBy(option.key as "latest" | "views")}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    sortBy === option.key
                      ? "bg-background-50 text-foreground-950"
                      : "text-foreground-500 hover:text-foreground-800"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {paged.length > 0 ? (
          <div className="flex flex-col gap-4">
            {paged.map((article) => (
              <ArticleRow key={article.id} article={article} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-foreground-300" />
            <p className="mt-4 text-base font-medium text-foreground-800">暂时没有符合条件的内容</p>
            <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或浏览其他频道</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                <i className="ri-home-4-line" />
                返回首页
              </Link>
              <button
                type="button"
                onClick={() => {
                  setTimeRange("all");
                  updateTag("");
                }}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
              >
            <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="flex h-10 cursor-pointer items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line" />
              上一页
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setPage(num)}
                className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  page === num
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="flex h-10 cursor-pointer items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 disabled:cursor-not-allowed disabled:opacity-40"
            >
              下一页
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}